import { Fragment, useCallback, useEffect, useState } from 'react';
import { Button } from '@cuito/ui';
import { locationsApi, type ShopView } from '../api/locations';
import EntryCodePanel from '../components/shelf/EntryCodePanel';
import { readServerMessage } from '../lib/apiError';
import { toggledShopId } from '../lib/locationList';

/**
 * 门店管理（`04-api-contract.md` §6.1 `GET /shops` 与地点码）。
 *
 * 取数走 `locationsApi.listShops()`（Task 17 的交付物），**不再自己拼 `apiClient.get`**：
 * 这里曾经另立了一个本地 `Shop` 接口，于是同一个响应形状在仓库里有两份声明，
 * 而 Task 17 交付的 `ShopView` 与 `listShops` 零调用（死代码）——两份类型迟早
 * 在字段增删时漂移，而漂移的那一份不会有任何编译错误提示。
 */
export default function ShopsPage() {
  const [shops, setShops] = useState<ShopView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 「地点码」展开的是哪一行；null 表示全部收起（单开语义见 toggledShopId）
  const [expandedShopId, setExpandedShopId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setShops(await locationsApi.listShops());
    } catch (e) {
      // 与同阶段其它页面同口径：后端原话优先（例如权限不足的具体原因）
      setError(readServerMessage(e) ?? '加载门店失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">门店管理</h1>

      {loading && <p className="text-gray-500">加载中...</p>}
      {error && <p className="font-bold text-red-600">{error}</p>}

      {!loading && !error && shops.length === 0 && <p className="text-gray-500">暂无门店</p>}

      {!loading && !error && shops.length > 0 && (
        <table className="w-full border-2 border-black">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">编号</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">名称</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">地址</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">状态</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">操作</th>
            </tr>
          </thead>
          <tbody>
            {shops.map((shop) => {
              const isExpanded = expandedShopId === shop.id;
              return (
                // Fragment 带 key：展开面板是**同一张表的第二个 <tr>**，
                // 塞进单元格里会让 <div> 成为 <td> 的子节点，列宽与表头对不齐
                <Fragment key={shop.id}>
                  <tr>
                    <td className="border-b border-gray-200 px-4 py-3">{shop.code}</td>
                    <td className="border-b border-gray-200 px-4 py-3">{shop.name}</td>
                    <td className="border-b border-gray-200 px-4 py-3">{shop.address ?? '-'}</td>
                    <td className="border-b border-gray-200 px-4 py-3">
                      {shop.status === 'ACTIVE' ? '营业中' : '已停业'}
                    </td>
                    <td className="border-b border-gray-200 px-4 py-3">
                      {/*
                        用 size="sm" 拿小字号与窄内边距，但显式补回 min-h-[44px]：
                        sm 自带的 min-h-[36px] 不达仓库约定的 44px 触控目标。
                        两者能共存是因为 Tailwind 对任意值按数值排序产出
                        （.min-h-[36px] 在前、.min-h-[44px] 在后），同为单类选择器时
                        生效的是后者，与 class 属性里的先后无关。
                      */}
                      <Button
                        size="sm"
                        variant="secondary"
                        className="min-h-[44px]"
                        aria-expanded={isExpanded}
                        onClick={() => setExpandedShopId(toggledShopId(expandedShopId, shop.id))}
                      >
                        {isExpanded ? '收起' : '地点码'}
                      </Button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={5} className="border-b border-gray-200 bg-gray-50 px-4 py-4">
                        {/* 面板自己取数并处理加载/错误/占位；只在展开时挂载，
                            收起即卸载，重新展开会重新拉一次，避免展示过期二维码 */}
                        <EntryCodePanel shopId={shop.id} shopName={shop.name} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      <Button className="mt-6" onClick={() => void load()} disabled={loading}>
        刷新
      </Button>
    </div>
  );
}
